import { DynamicModuleService } from "@/services/APIs";
import {
  mergeSchoolProfileRecordSources,
  flattenSchoolProfileRecord,
} from "@/project/karuna/utils/fetchSchemaGridRecords";
import {
  mapSchoolGridRecordToListItem,
  mapSchoolRecordToDisplayFields,
  type SchoolDisplayFields,
} from "@/project/karuna/utils/schoolProfileRecordFields";
import { mapWithConcurrency } from "@/project/karuna/utils/mapWithConcurrency";

export const SCHOOL_PROFILE_SCHEMA_NAME = "School Profile";

/** Card page: higher concurrency than bulk import (small batch only). */
const PAGE_ENRICHMENT_CONCURRENCY = 16;

const schoolListItemCache = new Map<
  string,
  { id: string } & SchoolDisplayFields
>();

export function unwrapDynamicRecord(
  raw: unknown
): Record<string, unknown> | null {
  if (raw === null || raw === undefined || typeof raw !== "object") {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const inner = r.data ?? r.result ?? r;
  if (inner === null || inner === undefined || typeof inner !== "object") {
    return null;
  }
  return inner as Record<string, unknown>;
}

export function schoolDisplayNeedsEnrichment(
  school: Pick<
    SchoolDisplayFields,
    "principal_name" | "kendra_name" | "email" | "contact_number"
  >
): boolean {
  return (
    school.principal_name === "N/A" ||
    school.kendra_name === "N/A" ||
    school.email === "N/A" ||
    school.contact_number === "N/A"
  );
}

export function getCachedSchoolListItem(
  id: string
): ({ id: string } & SchoolDisplayFields) | undefined {
  return schoolListItemCache.get(id);
}

export function primeSchoolListItemCache(
  school: { id: string } & SchoolDisplayFields
) {
  if (!schoolDisplayNeedsEnrichment(school)) {
    schoolListItemCache.set(school.id, school);
  }
}

export function clearSchoolListItemCache() {
  schoolListItemCache.clear();
}

/** Skip getItemById when the list row already has nested form values. */
export function listRecordHasDisplayData(
  listRecord?: Record<string, unknown>
): boolean {
  if (!listRecord) {
    return false;
  }
  const fields = mapSchoolRecordToDisplayFields(listRecord);
  return !schoolDisplayNeedsEnrichment(fields);
}

export async function fetchEnrichedSchoolListItem(
  school: { id: string } & SchoolDisplayFields,
  listRecord?: Record<string, unknown>
): Promise<{ id: string } & SchoolDisplayFields> {
  const cached = schoolListItemCache.get(school.id);
  if (cached && !schoolDisplayNeedsEnrichment(cached)) {
    return cached;
  }

  if (!schoolDisplayNeedsEnrichment(school)) {
    schoolListItemCache.set(school.id, school);
    return school;
  }

  if (listRecordHasDisplayData(listRecord)) {
    const fromList = mapSchoolGridRecordToListItem(listRecord!) ?? school;
    if (!schoolDisplayNeedsEnrichment(fromList)) {
      schoolListItemCache.set(school.id, fromList);
      return fromList;
    }
  }

  try {
    const raw = await DynamicModuleService.getItemById(
      SCHOOL_PROFILE_SCHEMA_NAME,
      school.id
    );
    const detail = unwrapDynamicRecord(raw);
    if (!detail) {
      return school;
    }

    const merged = mergeSchoolProfileRecordSources(listRecord ?? {}, detail);
    const enriched = mapSchoolGridRecordToListItem(merged) ?? school;
    if (!schoolDisplayNeedsEnrichment(enriched)) {
      schoolListItemCache.set(school.id, enriched);
    }
    return enriched;
  } catch {
    return school;
  }
}

/** Progressive: invoke onItemEnriched as each school finishes (cards update immediately). */
export async function enrichSchoolListItemsProgressively<
  T extends { id: string } & SchoolDisplayFields,
>(
  schools: T[],
  listRecordsById: Map<string, Record<string, unknown>>,
  onItemEnriched: (school: T) => void
): Promise<void> {
  const needsEnrichment = schools.filter(school => {
    const cached = schoolListItemCache.get(school.id);
    if (cached && !schoolDisplayNeedsEnrichment(cached)) {
      onItemEnriched(cached as T);
      return false;
    }
    return schoolDisplayNeedsEnrichment(school);
  });

  if (needsEnrichment.length === 0) {
    return;
  }

  await mapWithConcurrency(
    needsEnrichment,
    PAGE_ENRICHMENT_CONCURRENCY,
    async school => {
      const listRecord = listRecordsById.get(school.id);
      const enriched = (await fetchEnrichedSchoolListItem(
        school,
        listRecord
      )) as T;
      onItemEnriched(enriched);
    }
  );
}

export async function enrichSchoolListItemsForPage<
  T extends { id: string } & SchoolDisplayFields,
>(
  schools: T[],
  listRecordsById?: Map<string, Record<string, unknown>>
): Promise<T[]> {
  const byId = new Map<string, T>(schools.map(school => [school.id, school]));

  await enrichSchoolListItemsProgressively(
    schools,
    listRecordsById ?? new Map(),
    enriched => {
      byId.set(enriched.id, enriched);
    }
  );

  return schools.map(school => byId.get(school.id) ?? school);
}

export function buildSchoolListRecordMap(
  records: unknown[] | undefined
): Map<string, Record<string, unknown>> {
  const map = new Map<string, Record<string, unknown>>();
  if (!records || !Array.isArray(records)) {
    return map;
  }

  for (const record of records) {
    if (!record || typeof record !== "object") {
      continue;
    }
    const row = record as Record<string, unknown>;
    const id = String(row.id ?? row.pk ?? row._id ?? "").trim();
    if (id) {
      map.set(id, flattenSchoolProfileRecord(row));
    }
  }

  return map;
}
